import React, { useState } from 'react';
import './LoreDisplay.css';
import QubitHarvestingAnimation from './QubitHarvestingAnimation';
import { QubitHarvestingSystem } from '../../../utils/game/qubits';
import { PuzzleManager } from '../../../utils/game/puzzles';
import PuzzleDisplay from '../../../utils/game/puzzles/PuzzleDisplay';
import LoreSolverModal from './LoreSolverModal';
import accountStore from '../../../components/account/AccountStore';
import LoreService from '../../../services/LoreService';
import { useLore } from './LoreProvider';

const QuantumDataDisplay = ({ analysisData, onClose }) => {
  const { loreData, playerProgress, refreshProgress } = useLore();
  const [activeTab, setActiveTab] = useState('quantum');
  const [showHarvesting, setShowHarvesting] = useState(false);
  const [harvestResults, setHarvestResults] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [packetContent, setPacketContent] = useState(null);
  const [activePuzzle, setActivePuzzle] = useState(null);
  const [activeLore, setActiveLore] = useState(null);
  const [showLoreSolver, setShowLoreSolver] = useState(false);
  const harvestingSystem = React.useMemo(() => new QubitHarvestingSystem(), []);
  const puzzleManager = React.useMemo(() => new PuzzleManager(), []);

  const handleLoreSolved = async ({ solution, completedAt }) => {
    const profile = accountStore.getProfile();
    if (profile?.playerId && activeLore) {
      try {
        // Update lore progress in Firebase
        await LoreService.updateLoreProgress(
          profile.playerId,
          activeLore.id,
          {
            solution,
            completedAt
          }
        );

        // Get fresh data after update
        const freshData = await LoreService.getPlayerLoreProgress(profile.playerId);
       

        // Update local progress state
        await refreshProgress();

        // Get updated progress info using fresh data
        const userRank = accountStore.getRank();
        const targetTier = userRank - 1;
        const currentTier = `tier_${targetTier}`;
        const completedEntries = freshData?.[currentTier] || [];
        const totalLoreCount = Object.keys(loreData?.[currentTier] || {}).length;

        // Extract base IDs for display
        const baseIds = completedEntries.map(id => {
          const parts = id.split('_');
          return parts[parts.length - 1];
        });
       

        // Generate updated content
        const updatedContent = `[ENCRYPTED QUANTUM PACKET]
ORIGIN: REALITY LAYER ${analysisData.metadata.realityLayer}
TIMESTAMP: ${new Date(analysisData.metadata.timestamp).toLocaleString()}
STATUS: DECRYPTED

[CONTENT HASH: ${Math.random().toString(36).substring(2, 15).toUpperCase()}]
[DECRYPTION STATUS: SUCCESS]

[PROGRESS UPDATE]
- Completed Entries (${completedEntries.length}/${totalLoreCount}): ${baseIds.length > 0 ? baseIds.join(', ') : 'None'}
- Next Available: ${totalLoreCount - completedEntries.length} entries

[SCAN READY FOR NEXT ENTRY]`;

        // Update packet content with fresh data
        setPacketContent(updatedContent);

      } catch (error) {
        console.error('Error updating lore progress:', error);
      }
    }
    
    setShowLoreSolver(false);
    setActiveLore(null);
  };

  // Ensure animation cleanup after 5 seconds as a fallback
  React.useEffect(() => {
    if (showHarvesting) {
      const timer = setTimeout(() => {
        setShowHarvesting(false);
      }, 12000);
      
      return () => clearTimeout(timer);
    }
  }, [showHarvesting]);

  if (!analysisData) return null;

  const quantumSections = [
    {
      title: 'SIGNAL SUMMARY',
      data: analysisData.signalSummary || {},
      fields: [
        ['Class', (data) => data.class || 'N/A'],
        ['Strength', (data) => data.strength + '%' || 'N/A'],
        ['Stability', (data) => data.stability + '%' || 'N/A'],
        ['Quality', (data) => data.quality + '%' || 'N/A']
      ]
    },
    {
      title: 'QUANTUM STATE',
      data: analysisData.quantumState || {},
      fields: [
        ['Coherence', (data) => data.coherence || 'N/A'],
        ['Entanglement', (data) => data.entanglement || 'N/A'],
        ['Resonance', (data) => data.resonance || 'N/A']
      ]
    },
    {
      title: 'REWARDS',
      data: analysisData.rewards || {},
      fields: [
        ['Qubit Chance', (data) => data.qubitChance || 'N/A'],
        ['Multiplier', (data) => data.multiplier + 'x' || 'N/A'],
        ['Rarity', (data) => data.rarity || 'N/A']
      ]
    }
  ];

  const generatePacketContent = () => {
    const probabilities = accountStore.getContentProbabilities();
    const detectedContent = [];
    
    Object.entries(probabilities).forEach(([type, prob]) => {
      if (Math.random() < prob) {
        switch(type) {
          case 'quantumSignature':
            detectedContent.push('[QUANTUM SIGNATURE DETECTED]');
            detectedContent.push(`Coherence: ${analysisData.quantumState.coherence}`);
            break;
          case 'voidEcho':
            detectedContent.push('[VOID ECHO DETECTED]');
            detectedContent.push(`Resonance: ${analysisData.quantumState.resonance}`);
            break;
          case 'realityFragment':
            detectedContent.push('[REALITY FRAGMENT DETECTED]');
            detectedContent.push(`Layer: ${analysisData.metadata.realityLayer}`);
            break;
          case 'timelineData':
            detectedContent.push('[TIMELINE DATA DETECTED]');
            detectedContent.push(`Timestamp: ${new Date(analysisData.metadata.timestamp).toLocaleString()}`);
            break;
          case 'dimensionalRift':
            detectedContent.push('[DIMENSIONAL RIFT DETECTED]');
            detectedContent.push(`Class: ${analysisData.signalSummary.class}`);
            break;
        }
      }
    });

    const userRank = accountStore.getRank();
    const currentTier = `tier_${userRank}`;
    const completedEntries = playerProgress?.[currentTier] || [];
    const totalLoreCount = Object.keys(loreData?.[currentTier] || {}).length;

    // Extract base IDs from completed entries for better display
    const baseIds = completedEntries.map(id => {
      const parts = id.split('_');
      return parts[parts.length - 1];
    });

    return `[ENCRYPTED QUANTUM PACKET]
ORIGIN: REALITY LAYER ${analysisData.metadata.realityLayer}
TIMESTAMP: ${new Date(analysisData.metadata.timestamp).toLocaleString()}
STATUS: ENCRYPTED

[CONTENT HASH: ${Math.random().toString(36).substring(2, 15).toUpperCase()}]
[ENCRYPTION LEVEL: QUANTUM]
[DECRYPTION REQUIRED]

Current Progress:
- Completed Entries (${completedEntries.length}/${totalLoreCount}): ${baseIds.length > 0 ? baseIds.join(', ') : 'None'}
- Next Available: ${totalLoreCount - completedEntries.length} entries

${detectedContent.join('\n')}

[END OF PACKET]`;
  };

  const handlePuzzleComplete = async (result) => {
    const userRank = accountStore.getRank();
    const currentRank = Number(userRank);
    const profile = accountStore.getProfile();
    
    let newContent = `\n\n[DECRYPTION SUCCESSFUL]\n`;
    newContent += `ORIGIN: REALITY LAYER ${analysisData.metadata.realityLayer}\n`;
    newContent += `TIMESTAMP: ${new Date(analysisData.metadata.timestamp).toLocaleString()}\n`;
    newContent += `STATUS: DECRYPTED\n\n`;

    if (currentRank >= 2 && loreData && profile?.playerId) {
      // Determine which tier to serve based on rank
      let tierContent;
      let tierId;
      
      // Players should be able to access content from their current rank's tier
      // For example, rank 3 player should be able to access tier 2 content
      const targetTier = currentRank - 1;
      if (targetTier >= 1 && targetTier <= 4) {
        tierContent = loreData[`tier_${targetTier}`];
        tierId = `tier_${targetTier}`;
      }
      
      if (tierContent) {
        // Get available lore entries that haven't been completed
        const completedEntries = playerProgress?.[tierId] || [];
        

        // Extract base IDs from completed entries
        const baseIds = completedEntries.map(id => {
          const parts = id.split('_');
          return parts[parts.length - 1];
        });
       

        // Filter out completed entries using base IDs
        const availableLore = Object.entries(tierContent)
          .filter(([id]) => {
            const isCompleted = baseIds.includes(id);
            
            return !isCompleted;
          })
          .map(([id, content]) => ({
            id: `${tierId}_${id}`,
            baseId: id,
            ...content
          }));

        

        if (availableLore.length > 0) {
          // Select random unfinished lore entry
          const selectedContent = availableLore[Math.floor(Math.random() * availableLore.length)];
          
          // Set active lore for solver
          setActiveLore(selectedContent);
          setShowLoreSolver(true);
          
          // Add lore content to packet with progress info
          newContent += `[ENCRYPTED CONTENT DETECTED]\n===================\n`;
          newContent += `TIER ${tierId.split('_')[1]} PROGRESS:\n`;
          newContent += `- Completed: ${completedEntries.length}/${Object.keys(tierContent).length} entries\n`;
          newContent += `- Remaining: ${Object.keys(tierContent).length - completedEntries.length} entries\n`;
          newContent += `- Completed Entries: ${completedEntries.length > 0 ? completedEntries.join(', ') : 'None'}\n\n`;
          newContent += `${selectedContent.prompt}\n\n`;
          newContent += `MESSAGE: ${selectedContent.message}\n`;
          if (selectedContent.expanded) {
            newContent += `EXPANDED: ${selectedContent.expanded}\n`;
          }
          newContent += `HINT: ${selectedContent.hint}\n`;
          newContent += `===================\n`;
          newContent += `[END OF ENCRYPTED CONTENT]\n\n`;
          newContent += `[CIPHER SOLVER ACTIVATED]`;
        } else {
          newContent += `[SCAN COMPLETE]\nAll encrypted content for ${tierId.replace('_', ' ')} has been decrypted.\n`;
          newContent += `Progress: ${completedEntries.length}/${Object.keys(tierContent).length} entries completed.\n`;
          newContent += `Await rank increase for access to higher tier content.`;
        }
      }
    } else {
      newContent += `[SCAN COMPLETE]\nNo encrypted content detected in packet.\nPacket was clean.\n\n`;
      newContent += `Note: Encrypted content access is based on your rank:\n`;
      newContent += `- Rank 2 unlocks Tier 1 content\n`;
      newContent += `- Rank 3 unlocks Tier 2 content\n`;
      newContent += `- Rank 4 unlocks Tier 3 content\n`;
      newContent += `- Rank 5 unlocks Tier 4 content\n`;
      newContent += `You can always access content from your current tier.\n`;
      newContent += `\nYour current rank: ${userRank}`;
    }
    
    setPacketContent(prev => {
      const updated = prev + newContent;
      return updated;
    });
    setActivePuzzle(null);
  };

  const handleScan = () => {
    setIsScanning(true);

    // Refresh player data before scanning
    const profile = accountStore.getProfile();
    if (profile?.playerId) {
      accountStore.refreshProfile()
        .then(freshProfile => {
          if (freshProfile && freshProfile.data) {
            // Generate puzzle after fresh data
            const generatedPuzzle = puzzleManager.tryTriggerPuzzle();

            if (generatedPuzzle) {
              setActivePuzzle(generatedPuzzle);
            }

            // Store the content when revealed
            const revealPacketContent = () => {
              const content = generatePacketContent();
              setPacketContent(content);
              setIsScanning(false);
            };

            if (!generatedPuzzle) {
              revealPacketContent();
            }
          }
        })
        .catch(error => {
          console.error('Error refreshing profile:', error);
          setIsScanning(false);
        });
    } else {
      // If no profile, continue without refresh
      const generatedPuzzle = puzzleManager.tryTriggerPuzzle();

      if (generatedPuzzle) {
        setActivePuzzle(generatedPuzzle);
      }

      // Store the content when revealed
      const revealPacketContent = () => {
        const content = generatePacketContent();
        setPacketContent(content);
        setIsScanning(false);
      };

      if (!generatedPuzzle) {
        revealPacketContent();
      }
    }
  };

  const renderSection = (section) => (
    <div key={section.title} className="quantum-data-section">
      <h3 className="quantum-data-title">{'>'} {section.title}</h3>
      <div className="quantum-data-fields">
        {section.fields.map(([label, valueAccessor]) => {
          const value = typeof valueAccessor === 'function'
            ? valueAccessor(section.data)
            : section.data[valueAccessor] || 'N/A';
          
          return (
            <div key={label} className="quantum-data-field">
              <span className="quantum-data-label">{label}:</span>
              <span className="quantum-data-value">
                {value === 'N/A' ? (
                  <span style={{ color: 'rgba(0, 255, 0, 0.5)' }}>NO_DATA</span>
                ) : value}
              </span>
            </div>
          );
        })}
        {section.customRender && section.customRender(section.data)}
      </div>
    </div>
  );

  const renderTabContent = () => {
    const getContent = () => {
      switch (activeTab) {
        case 'quantum':
          return (
            <div className="quantum-data-grid">
              {quantumSections.map(renderSection)}
            </div>
          );
        
        case 'packet':
          return (
            <div className="packet-content">
              {packetContent ? (
                <div className="packet-data">
                  <h3 className="quantum-data-title">{'>'} PACKET CONTENTS</h3>
                  <pre className="packet-text">{packetContent}</pre>
                </div>
              ) : (
                <div className="packet-scan-container">
                  <button
                    className="scan-button"
                    onClick={handleScan}
                    disabled={isScanning}
                  >
                    {isScanning ? (
                      <span className="quantum-spinner" role="status" aria-label="Scanning" />
                    ) : (
                      'SCAN FOR CONTENT'
                    )}
                  </button>
                </div>
              )}
            </div>
          );
        
        default:
          return null;
      }
    };

    return (
      <div className="quantum-tab-content" key={activeTab}>
        {getContent()}
      </div>
    );
  };

  return (
    <div className="lore-display-overlay">
      <div className="lore-display-container">
        <div className="lore-display-border">
          <div className="lore-display-content">
            <h2 className="lore-display-header">
              PACKET DATA
            </h2>
            
            <div className="quantum-tabs">
              <div className="quantum-tab-header">
                <button
                  className={`quantum-tab ${activeTab === 'quantum' ? 'active' : ''}`}
                  onClick={() => setActiveTab('quantum')}
                >
                  PACKET_METRICS
                </button>
                <button
                  className={`quantum-tab ${activeTab === 'packet' ? 'active' : ''}`}
                  onClick={() => setActiveTab('packet')}
                >
                  PACKET_CONTENT
                </button>
              </div>
              <div className="quantum-tab-content">
                {renderTabContent()}
              </div>
            </div>

            <div className="lore-display-separator" />
            
            <div className="quantum-metadata">
              <div className="metadata-left">
                <div>ID: {analysisData.metadata.readingId}</div>
                <div>Time: {new Date(analysisData.metadata.timestamp).toLocaleTimeString()}</div>
                <div>Layer: {analysisData.metadata.realityLayer}</div>
              </div>
              <div className="metadata-right">
                <div className="signal-stats">
                  <div className="signal-quality">
                    Signal Quality: {analysisData.signalSummary.quality}%
                  </div>
                  <div className="qubit-chance">
                    Qubit Chance: {analysisData.rewards.qubitChance}
                  </div>
                  <div className="signal-class">
                    Class: {analysisData.signalSummary.class}
                  </div>
                </div>
              </div>
            </div>

            <div className="quantum-buttons-container">
              <button
                className="lore-display-button"
                onClick={() => {
                  if (!analysisData?.signalSummary || !analysisData?.quantumState) {
                    console.warn('No quantum data available for harvesting');
                    return;
                  }

                  if (harvestResults !== null) {
                    // If we've already clicked once, close the modal
                    onClose();
                    return;
                  }

                  // Set harvestResults immediately to change button state
                  setHarvestResults({ pending: true });

                  // Format and validate the data with enhanced precision
                  const harvestData = {
                    signalSummary: {
                      class: analysisData.signalSummary.class,
                      strength: parseFloat(analysisData.signalSummary.strength),
                      stability: parseFloat(analysisData.signalSummary.stability),
                      quality: parseFloat(analysisData.signalSummary.quality)
                    },
                    quantumState: {
                      coherence: parseFloat(analysisData.quantumState.coherence),
                      entanglement: parseFloat(analysisData.quantumState.entanglement),
                      resonance: parseFloat(analysisData.quantumState.resonance)
                    },
                    rewards: {
                      qubitChance: parseFloat(analysisData.rewards.qubitChance),
                      multiplier: parseFloat(analysisData.rewards.multiplier),
                      rarity: analysisData.rewards.rarity
                    },
                    particleActivity: analysisData.particleActivity ? {
                      type: analysisData.particleActivity.type,
                      energy: parseFloat(analysisData.particleActivity.energy),
                      confidence: parseFloat(analysisData.particleActivity.confidence)
                    } : null,
                    cosmicEvents: analysisData.cosmicEvents ? {
                      count: parseInt(analysisData.cosmicEvents.count),
                      significance: parseFloat(analysisData.cosmicEvents.significance),
                      types: analysisData.cosmicEvents.types
                    } : null,
                    alerts: analysisData.alerts || [],
                    metadata: {
                      readingId: analysisData.metadata.readingId,
                      timestamp: analysisData.metadata.timestamp,
                      realityLayer: analysisData.metadata.realityLayer
                    }
                  };

                  // Start harvesting process
                  setShowHarvesting(true);
                  harvestingSystem.harvestQubits(harvestData)
                    .then(results => {
                      // Ensure results are safe for animation
                      const safeResults = {
                        qubits: parseInt(results.qubits) || 20,
                        bonuses: Array.isArray(results.bonuses) ? results.bonuses : [],
                        totalMultiplier: parseFloat(results.totalMultiplier) || 1,
                        totalHarvested: parseInt(results.totalHarvested) || 0
                      };
                      setHarvestResults(safeResults);
                    })
                    .catch(error => {
                      console.error('Error during qubit harvesting:', error);
                      // Provide safe fallback values on error
                      setHarvestResults({
                        qubits: 20,
                        bonuses: [],
                        totalMultiplier: 1,
                        totalHarvested: 0
                      });
                    });
                }}
                disabled={showHarvesting}
              >
                {showHarvesting ? (
                  <span className="quantum-spinner" role="status" aria-label="Loading" />
                ) : harvestResults ? 'CLOSE' : 'PROCESS PACKET'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showHarvesting && (
        <div className="harvest-overlay">
          <QubitHarvestingAnimation
            results={harvestResults}
            onComplete={() => {
              setShowHarvesting(false);
              // Don't reset harvestResults to keep button in CLOSE state
            }}
          />
        </div>
      )}
      {activePuzzle && (
        <PuzzleDisplay
          puzzle={activePuzzle}
          onComplete={handlePuzzleComplete}
          onClose={() => setActivePuzzle(null)}
        />
      )}
      {showLoreSolver && activeLore && (
        <LoreSolverModal
          loreContent={activeLore}
          onSolve={handleLoreSolved}
          onClose={() => {
            setShowLoreSolver(false);
            setActiveLore(null);
          }}
        />
      )}
    </div>
  );
};

export default QuantumDataDisplay;